import Button from '@rio-cloud/rio-uikit/Button';
import type { ComponentProps } from 'react';
import type { FieldErrors } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { ValidationViolation } from '#components/form/ValidationViolation.tsx';
import { SortableTourList } from '#components/tourStops/SortableTourList.tsx';
import type { Order } from '#domain/Order.ts';
import { isChargingStop } from '#domain/TourStop.ts';

import type { TourFormValues } from '../model/NewTourFormState.ts';
import type { RoutingSettings } from '../model/routingSettings.ts';
import type { SmartRouteResult } from '../service/smartRoute.ts';
import { OpenOrdersList } from './OpenOrdersList.tsx';
import { SmartRouteSummary } from './SmartRouteSummary.tsx';
import { TourStopsEmptyState } from './TourStopsEmptyState.tsx';
import { TourStopsMap } from './TourStopsMap.tsx';
import { VehicleAvailabilityTimeline } from './VehicleAvailabilityTimeline.tsx';

type Props = {
    openOrdersProps: ComponentProps<typeof OpenOrdersList>;
    stopListProps: Pick<
        ComponentProps<typeof SortableTourList>,
        | 'sortableTourStops'
        | 'setSortableTourStops'
        | 'misplacedStopIds'
        | 'onRemoveStop'
        | 'editableStopIds'
        | 'onEditStop'
        | 'capacity'
    > & { orders: Order[] };
    vehicleError?: FieldErrors<TourFormValues>['vehicle'];
    selectedVehicleId: string;
    onSelectVehicle: (vehicleId: string) => void;
    tourId?: string;
    isOpen: boolean;
    isLoading: boolean;
    isRouting: boolean;
    isTourStarted: boolean;
    showSmartRoute: boolean;
    onShowVehicleSettings: () => void;
    routingSettings: RoutingSettings;
    routeResult?: SmartRouteResult;
    onAcceptCharging?: () => void;
};

export const TourPlanningContent = ({
    openOrdersProps,
    stopListProps,
    vehicleError,
    selectedVehicleId,
    onSelectVehicle,
    tourId,
    isOpen,
    isLoading,
    isRouting,
    isTourStarted,
    showSmartRoute,
    onShowVehicleSettings,
    routingSettings,
    routeResult,
    onAcceptCharging,
}: Props) => {
    const intl = useIntl();
    return (
        <div
            className='tour-planning-grid gap-20 margin-top-20 align-items-stretch'
            data-testid='create-tour-content-grid'
        >
            <div className='tour-planning-column min-width-0'>
                <OpenOrdersList {...openOrdersProps} />
            </div>
            <section
                className='tour-planning-column min-width-0 display-flex flex-column'
                aria-label={intl.formatMessage({
                    id: 'intl-msg:ordercom-cloud-frontend.tour.stops',
                })}
            >
                <div className='display-flex justify-content-between align-items-center gap-10 margin-bottom-10'>
                    <h4 className='margin-0'>
                        {intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.tour.stops',
                        })}
                    </h4>
                    <span className='text-size-12 text-color-dark'>
                        {intl.formatMessage(
                            {
                                id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.stop-count',
                            },
                            { count: stopListProps.sortableTourStops.length }
                        )}
                    </span>
                </div>
                {routeResult && <SmartRouteSummary result={routeResult} routingSettings={routingSettings} />}
                {onAcceptCharging && (
                    <Button
                        className='margin-bottom-10'
                        bsStyle='primary'
                        onClick={onAcceptCharging}
                        disabled={isLoading || isRouting}
                    >
                        {intl.formatMessage({ id: 'intl-msg:ordercom-cloud-frontend.charging.accept' })}
                    </Button>
                )}
                {stopListProps.sortableTourStops.some(isChargingStop) && (
                    <div role='status' className='alert alert-warning'>
                        {intl.formatMessage({ id: 'intl-msg:ordercom-cloud-frontend.charging.routing-unverified' })}
                    </div>
                )}
                {stopListProps.sortableTourStops.some(stop => stop.chargingDraftId) && (
                    <p>{intl.formatMessage({ id: 'intl-msg:ordercom-cloud-frontend.charging.draft-times' })}</p>
                )}
                {stopListProps.sortableTourStops.length === 0 ? (
                    <TourStopsEmptyState />
                ) : (
                    <SortableTourList
                        {...stopListProps}
                        showSequenceMarkers
                        chargingSuggestions={routeResult?.chargingStops}
                        batteryCapacity={routingSettings.battery_kwh}
                        scrollable
                    />
                )}
            </section>
            <div className='tour-planning-sidebar min-width-0 gap-20'>
                <TourStopsMap
                    stops={stopListProps.sortableTourStops}
                    height={250}
                    routePositions={routeResult?.positions}
                    chargingStops={routeResult?.chargingStops}
                    batteryCapacity={routingSettings.battery_kwh}
                />
                <div
                    className={`tour-planning-vehicles form-group margin-0 min-width-0 display-flex flex-column ${vehicleError ? 'has-error has-feedback' : ''}`}
                >
                    <VehicleAvailabilityTimeline
                        vehicleSettingsAction={
                            showSmartRoute && (
                                <Button
                                    iconName='cog'
                                    variant='link'
                                    disabled={isLoading || isRouting}
                                    onClick={onShowVehicleSettings}
                                    aria-label={intl.formatMessage({
                                        id: 'intl-msg:ordercom-cloud-frontend.routing-settings.title',
                                    })}
                                />
                            )
                        }
                        stops={stopListProps.sortableTourStops}
                        selectedVehicleId={selectedVehicleId}
                        onSelectVehicle={onSelectVehicle}
                        excludedTourId={tourId}
                        disabled={isTourStarted}
                        showProposedTour={!isTourStarted}
                        skip={!isOpen}
                    />
                    <ValidationViolation violations={vehicleError?.message} />
                </div>
            </div>
        </div>
    );
};
